import React, {useState, useEffect} from "react";
import { TourOperator, TourCatalog } from "./contentfulModel.ts";
import classNames from "classnames";
import {compareMiceSubcategories, isMiceCatalog} from "./utils.tsx";

// Arrow left icon component
const ArrowLeftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"/>
    </svg>
);

// Arrow down icon component for collapsible sections
const ArrowDownIcon = ({ isExpanded }: { isExpanded: boolean }) => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
        className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : 'rotate-0'}`}
    >
        <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
    </svg>
);

// Arrow right icon component
const ArrowRightIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd"
              d="M19.8848 10.0001C19.8848 10.7344 19.3284 11.3296 18.642 11.3296L1.24315 11.3296C0.55678 11.3296 0.000371985 10.7344 0.000372049 10.0001C0.000372113 9.26586 0.55678 8.67063 1.24315 8.67063L18.642 8.67063C19.3284 8.67063 19.8848 9.26586 19.8848 10.0001Z"
              fill="currentColor"/>
        <path fill-rule="evenodd" clip-rule="evenodd"
              d="M9.25952 19.6339C8.78297 19.1457 8.78297 18.3543 9.25952 17.8661L16.9387 10L9.25952 2.13388C8.78297 1.64573 8.78297 0.854272 9.25952 0.366118C9.73608 -0.122039 10.5087 -0.122039 10.9853 0.366119L19.5274 9.11612C20.0039 9.60427 20.0039 10.3957 19.5274 10.8839L10.9853 19.6339C10.5087 20.122 9.73608 20.122 9.25952 19.6339Z"
              fill="currentColor"/>
    </svg>
);

interface TourCatalogIndexProps {
    tourCatalog: TourCatalog;
    goToSlide: (slideName: string) => void;
    tourOperator?: TourOperator;
}

export const TourCatalogIndex: React.FC<TourCatalogIndexProps> = ({
                                                                      tourCatalog,
                                                                      goToSlide,
                                                                      tourOperator
                                                                  }) => {
    const [activeCategory, setActiveCategory] = useState<string | null>(null);
    const [activeSubcategory, setActiveSubcategory] = useState<string | null>(null);
    const [expandedSubcategories, setExpandedSubcategories] = useState<Set<string>>(new Set());
    const [isMobile] = useState(window.innerWidth < 768);
    const [categories, setCategories] = useState<{name: string, hasContent: boolean}[]>([]);

    // Get operator color
    const operatorColor = tourOperator?.primaryColor || '#3B82F6';


    // Function to toggle subcategory expansion
    const toggleSubcategoryExpansion = (subcategory: string) => {
        const newExpanded = new Set(expandedSubcategories);
        if (newExpanded.has(subcategory)) {
            newExpanded.delete(subcategory);
        } else {
            newExpanded.add(subcategory);
        }
        setExpandedSubcategories(newExpanded);
    };

    // Function to get subcategories for a category
    const getSubcategoriesForCategory = (categoryName: string) => {
        const allPackages = tourCatalog.tourPackages || [];
        const subcategoriesSet = new Set<string>();

        allPackages.forEach(pkg => {
            const pkgCategory = pkg.category || 'Circuitos';
            if (pkgCategory === categoryName && pkg.subcategory) {
                subcategoriesSet.add(pkg.subcategory);
            }
        });

        return Array.from(subcategoriesSet);
    };

    // Function to handle category click
    const handleCategoryClick = (category: string) => {
        const cat = categories.find(cat => cat.name === category);
        if (cat && !cat.hasContent) {
            goToSlide(category);
            return;
        }

        const subcategories = getSubcategoriesForCategory(category);

        // If category has subcategories, show them
        if (subcategories.length > 0) {
            setActiveCategory(activeCategory === category ? null : category);
            setActiveSubcategory(null); // Reset subcategory when changing category
        } else {
            // If no subcategories, show packages directly
            setActiveCategory(activeCategory === category ? null : category);
            setActiveSubcategory(null);
        }
    };



    // Function to handle going back to index
    const handleBackToIndex = () => {
        setActiveCategory(null);
        setActiveSubcategory(null);
    };




    useEffect(() => {
        const allPackages = tourCatalog.tourPackages || [];
        const uniqueCategories = new Set<string>();

        // Add all categories from packages
        allPackages.forEach(pkg => {
            if (pkg.category) {
                uniqueCategories.add(pkg.category);
            } else {
                // Default category if not specified
                uniqueCategories.add('Circuitos');
            }
        });

        // If no categories found, add default ones
        if (uniqueCategories.size === 0) {
            uniqueCategories.add('Circuitos');
        }

        if(tourCatalog.otherContents && tourCatalog.otherContents.length > 0){
            tourCatalog.otherContents.forEach(content => {
                if(!content.category){
                    uniqueCategories.add(content.title);
                }
            });
        }
        if(tourCatalog.hotels && tourCatalog.hotels.length > 0){
                uniqueCategories.add("Hoteles");
        }

        setCategories(Array.from(uniqueCategories).map(name => ({
            name,
            hasContent: allPackages.some(pkg => (pkg.category || 'Circuitos') === name)
        })).sort((a, b) => {
            //sort by tourcatalog.tourPackagesCategoryOrder
            const order = tourCatalog.tourPackagesCategoryOrder || [];
            //if no include in order, put at the end
            if (!order.includes(a.name)) return 1;
            if (!order.includes(b.name)) return -1;
            return (order.indexOf(a.name) - order.indexOf(b.name))
        }));
    }, [tourCatalog]);


    return (
        <div className="flex flex-col md:flex-row w-full h-full flex-col-reverse gap-0 overflow-y-scroll">
            {/* Left Section - 50% width */}
            <div className="w-full md:w-1/2  p-6 md:p-6 text-left flex flex-col justify-between  h-full md:overflow-y-scroll md:pt-[100px] md:px-[70px]">
                <div>
                {/* Back to index button - shown only when a category is active */}
                {activeCategory && (
                    <div
                        onClick={handleBackToIndex}
                        className="bg-white md:bg-white pl-4 ml-0 p-3 rounded-xl absolute md:relative cursor-pointer flex items-center gap-2 text-black hover:underline top-3 md:top-auto md:mb-6 opacity-85 left-4 md:left-auto w-auto self-start md:max-w-50"
                        style={{
                            boxShadow: isMobile ? "0px 4px 4px 0px #00000040" : ""
                        }}
                    >
                        <ArrowLeftIcon />
                        <span className="text-sm font-semibold">Volver al índice</span>
                    </div>
                )}

                <div className="mb-4 md:mb-6">
                    <h3 className="text-sm font-normal uppercase text-gray-600 mb-2">
                        Todos nuestros Itinerarios
                    </h3>
                    <h2 className="text-2xl md:text-4xl font-bold">
                        {tourCatalog.title}
                    </h2>
                </div>

                {/* Main Index Categories */}
                <div id="parentIndx" className="mt-4 md:mt-8">
                    <ul className="space-y-4 pl-0">
                        {categories.map((category, idx) => (
                            (activeCategory === null || activeCategory === category.name) && (
                                <li key={idx}>
                                    {/* Category Item */}
                                    <div
                                        className={classNames([
                                            "cursor-pointer text-lg md:text-xl font-medium cursor-pointer flex justify-between items-center pb-3",
                                            !activeCategory ? "border-b-1" : ""
                                        ])}
                                        onClick={() => handleCategoryClick(category.name)}
                                    >
                                        <span className="uppercase">{category.name}</span>
                                        {!activeCategory ?
                                            <span style={{color: operatorColor}}>
                                            <ArrowRightIcon/>
                                        </span> : null}
                                    </div>

                                    {/* Subcategories Menu */}
                                    {activeCategory === category.name && (() => {
                                        const subcategories = getSubcategoriesForCategory(category.name);
                                        const packagesWithoutSubcategory = tourCatalog.tourPackages.filter(pkg => {
                                            const pkgCategory = pkg.category || 'Circuitos';
                                            return pkgCategory === category.name && !pkg.subcategory;
                                        });

                                        return subcategories.length > 0 ? (
                                            <div className="ml-4 mt-2 space-y-4">
                                                {/* Packages without subcategory first */}
                                                {packagesWithoutSubcategory.sort(
                                                    (a, b) => (a.countryCodes > b.countryCodes ? 1 : -1)
                                                ).map((tourPackage, pkgIdx) => (
                                                    <div key={`no-sub-${pkgIdx}`} className="space-y-2">
                                                        {pkgIdx === 0 && <div className="h-px bg-gray-200 w-full"></div>}
                                                        <div
                                                            className="cursor-pointer group"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                goToSlide((tourPackage.category + "-"+tourPackage.name+(tourPackage.subtitle ? "-"+tourPackage.subtitle : "")));
                                                            }}
                                                        >
                                                            <div className="flex gap-3 items-center py-1">
                                                                <div className="w-12 h-12 flex-shrink-0 overflow-hidden rounded">
                                                                    {tourPackage.introductionImage && (
                                                                        <img crossOrigin="anonymous"
                                                                            src={tourPackage.introductionImage?.file}
                                                                            alt={tourPackage.name}
                                                                            className="w-full h-full object-cover"
                                                                        />
                                                                    )}
                                                                </div>
                                                                <div className="flex-grow">
                                                                    <h5 className="text-sm md:text-base uppercase mb-0">{tourPackage.name}</h5>
                                                                    {tourPackage.subtitle && (
                                                                        <p className="text-xs text-gray-600">{tourPackage.subtitle}</p>
                                                                    )}
                                                                </div>
                                                                <div style={{ color: operatorColor }}>
                                                                    <ArrowRightIcon />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}

                                                {/* Subcategories with their packages */}
                                                {subcategories.sort(
                                                    (a, b) => isMiceCatalog(tourCatalog) ? compareMiceSubcategories(a, b) : (a < b ? 1 : -1)
                                                ).map((subcategory, subIdx) => {
                                                    const subcategoryPackages = tourCatalog.tourPackages.filter(pkg => {
                                                        const pkgCategory = pkg.category || 'Circuitos';
                                                        return pkgCategory === category.name && pkg.subcategory === subcategory;
                                                    });

                                                    return (
                                                        <div key={subIdx} className="space-y-2">
                                                            {/* Subcategory header */}
                                                            <div className="h-px bg-gray-200 w-full"></div>
                                                            <div className="text-base md:text-lg font-medium py-2 px-2  rounded cursor-pointer flex justify-between items-center"
                                                                 onClick={() => toggleSubcategoryExpansion(subcategory)}
                                                            >
                                                                <span className="uppercase ">{subcategory}</span>
                                                                <ArrowDownIcon isExpanded={expandedSubcategories.has(subcategory)} />
                                                            </div>

                                                            {/* Packages in this subcategory */}
                                                            {expandedSubcategories.has(subcategory) && subcategoryPackages.sort(
                                                                (a, b) => (a.countryCodes > b.countryCodes ? 1 : -1)
                                                            ).map((tourPackage, pkgIdx) => (
                                                                <div
                                                                    key={pkgIdx}
                                                                    className="cursor-pointer group ml-2"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        goToSlide((tourPackage.category + "-"+tourPackage.name+(tourPackage.subtitle ? "-"+tourPackage.subtitle : "")));
                                                                    }}
                                                                >
                                                                    <div className="flex gap-3 items-center py-1">
                                                                        <div className="w-12 h-12 flex-shrink-0 overflow-hidden rounded">
                                                                            {tourPackage.introductionImage && (
                                                                                <img crossOrigin="anonymous"
                                                                                    src={tourPackage.introductionImage?.file}
                                                                                    alt={tourPackage.name}
                                                                                    className="w-full h-full object-cover"
                                                                                />
                                                                            )}
                                                                        </div>
                                                                        <div className="flex-grow">
                                                                            <h5 className="text-sm md:text-base uppercase mb-0">{tourPackage.name}</h5>
                                                                            {tourPackage.subtitle && (
                                                                                <p className="text-xs text-gray-600">{tourPackage.subtitle}</p>
                                                                            )}
                                                                        </div>
                                                                        <div style={{ color: operatorColor }}>
                                                                            <ArrowRightIcon />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : null;
                                    })()}
                                    {tourCatalog.otherContents.filter((otherC => {
                                    return otherC.category?.trim() ===activeCategory
                                    })).map((otherContent, index) => (
                                    <div
                                        key={index}
                                        id={otherContent.title}
                                        className="cursor-pointer group"
                                        onClick={() => goToSlide(otherContent.title)}
                                    >
                                        {/* Gray separator */}
                                        <div className="h-px bg-gray-200 mb-4 w-full"></div>

                                        <div className="flex gap-4 items-center">


                                            {/* Other content title and subtitle */}
                                            <div className="flex-grow">
                                                <h4 className="text-md md:text-lg uppercase mb-1">{otherContent.title}</h4>
                                                {otherContent.subtitle && (
                                                    <p className="text-sm text-gray-600">{otherContent.subtitle}</p>
                                                )}
                                            </div>
                                            {/* Arrow indicator */}
                                            <div style={{ color: operatorColor }}>
                                                <ArrowRightIcon />
                                            </div>
                                        </div>
                                    </div>
                                    ))}
                                </li>
                            )
                        ))}
                    </ul>

                    {/* Tour packages for categories without subcategories */}
                    {activeCategory && (() => {
                        const subcategories = getSubcategoriesForCategory(activeCategory);
                        const hasSubcategories = subcategories.length > 0;

                        // Only show this section if there are no subcategories
                        if (hasSubcategories) return null;

                        const filteredPackages = tourCatalog.tourPackages.filter(pkg => {
                            const pkgCategory = pkg.category || 'Circuitos';
                            return pkgCategory === activeCategory;
                        });


                        return filteredPackages.length > 0 ? (
                            <div className="mt-0 space-y-6">
                                {filteredPackages.sort(
                                    (a, b) => (a.countryCodes > b.countryCodes ? 1 : -1)
                                ).map((tourPackage, index) => (
                                    <div
                                        id={tourPackage.name}
                                        key={index}
                                        className="cursor-pointer group"
                                        onClick={() => goToSlide((tourPackage.category + "-"+tourPackage.name+(tourPackage.subtitle ? "-"+tourPackage.subtitle : "")))}
                                    >
                                        {/* Gray separator */}
                                        <div className="h-px bg-gray-200 mb-4 w-full"></div>

                                        <div className="flex gap-4 items-center">
                                            {/* Tour package image */}
                                            <div className="w-20 h-20 flex-shrink-0 overflow-hidden rounded">
                                                {tourPackage.introductionImage && (
                                                    <img crossOrigin="anonymous"
                                                        src={tourPackage.introductionImage?.file}
                                                        alt={tourPackage.name}
                                                        className="w-full h-full object-cover"
                                                    />
                                                )}
                                            </div>

                                            {/* Tour package name and subtitle */}
                                            <div className="flex-grow">
                                                <h4 className="text-md md:text-lg uppercase mb-1">{tourPackage.name}</h4>
                                                {tourPackage.subtitle && (
                                                    <p className="text-sm text-gray-600">{tourPackage.subtitle}</p>
                                                )}
                                            </div>

                                            {/* Arrow indicator */}
                                            <div style={{ color: operatorColor }}>
                                                <ArrowRightIcon />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {tourCatalog.otherContents.filter((otherC => {
                                    return otherC.category?.trim() === activeCategory.trim()
                                })).map((otherContent, index) => (
                                    <div
                                        key={index}
                                        id={otherContent.title}
                                        className="cursor-pointer group"
                                        onClick={() => goToSlide(otherContent.title)}
                                    >
                                        {/* Gray separator */}
                                        <div className="h-px bg-gray-200 mb-4 w-full"></div>

                                        <div className="flex gap-4 items-center">
                                            {/* Other content image */}
                                            <div className="w-20 h-20 flex-shrink-0 overflow-hidden rounded">
                                                {otherContent.mainImage && (
                                                    <img crossOrigin="anonymous"
                                                        src={otherContent.mainImage?.file}
                                                        alt={otherContent.title}
                                                        className="w-full h-full object-cover"
                                                    />
                                                )}
                                            </div>

                                            {/* Other content title and subtitle */}
                                            <div className="flex-grow">
                                                <h4 className="text-md md:text-lg uppercase mb-1">{otherContent.title}</h4>
                                                {otherContent.subtitle && (
                                                    <p class Name="text-sm text-gray-600">{otherContent.subtitle}</p>
                                                )}
                                            </div>

                                            {/* Arrow indicator */}
                                            <div style={{ color: operatorColor }}>
                                                <ArrowRightIcon />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div>
                                <div className="h-px bg-gray-200 mb-4 w-full"></div>
                                <p className="text-base text-gray-500">No hay paquetes disponibles</p>
                            </div>
                        );
                    })()}
                </div>
                </div>
                { tourCatalog.partnerLogo ?
                <div className="pt-6">
                    <img src={tourCatalog.partnerLogo.file} alt="Partner Logo" className="w-30 md:w-40 md:mt-10  md:block"/>
                </div> : null }
            </div>

            {/* Right Section - 50% width with image */}
            <div className="w-full md:w-1/2 h-[120px] md:h-full md:rounded-l-[20px]">
                <img crossOrigin="anonymous"
                    src={tourCatalog.indexImage?.file || ""}
                    alt="Catalog preview"
                    className="w-full h-full object-cover rounded-b-2xl md:rounded-r-none md:rounded-l-[20px]"
                />
                <img crossOrigin="anonymous" src={!isMobile ? tourOperator.logoContrast.file : tourOperator.logo.file} className="w-40 md:absolute bottom-4 right-4 hidden md:block"
                 alt={tourOperator.name}/>
            </div>
        </div>
    );
};
